import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, retry, tap, throwError, timer } from 'rxjs';

import type { Me } from '@core/models/access.model';
import { AccessApiService } from './access-api.service';

/**
 * Loads and caches the authenticated identity (`GET /api/me`) for the active tenant.
 * Exposes `isBootstrapAdmin`, the platform "development user" flag used to gate the
 * control center (permisos, roles, menús, tenant) on the frontend.
 */
@Injectable({ providedIn: 'root' })
export class MeService {
  private readonly api = inject(AccessApiService);

  private readonly _me = signal<Me | null>(null);
  private readonly _loaded = signal(false);
  private readonly _loadError = signal(false);

  readonly me = this._me.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly loadError = this._loadError.asReadonly();

  /** True only for users listed as bootstrap admins on the backend (the development user). */
  readonly isBootstrapAdmin = computed(() => this._me()?.isBootstrapAdmin ?? false);

  load(): Observable<Me | null> {
    this._loadError.set(false);

    return this.api.getMe().pipe(
      retry({
        count: 2,
        delay: (error, retryCount) => this.retryTransientFailure(error, retryCount)
      }),
      tap(me => {
        this._me.set(me);
        this._loaded.set(true);
        this._loadError.set(false);
      }),
      catchError(() => {
        this._me.set(null);
        // A failed request is not a successfully loaded empty identity. Keeping
        // loaded=false allows guards and the layout retry action to recover.
        this._loaded.set(false);
        this._loadError.set(true);
        return of(null);
      })
    );
  }

  /** Loads identity once if not already cached; resolves immediately when cached. */
  ensureLoaded(): Observable<void> {
    if (this._loaded() && this._me()) {
      return of(undefined);
    }
    return this.load().pipe(map(() => undefined));
  }

  clear(): void {
    this._me.set(null);
    this._loaded.set(false);
    this._loadError.set(false);
  }

  private retryTransientFailure(error: unknown, retryCount: number): Observable<number> {
    if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
      return throwError(() => error);
    }

    return timer(retryCount * 500);
  }
}
